import { useState, useMemo, useRef, useEffect } from "react";
import { ChevronDown, Search, Check } from "lucide-react";

function methodChip(method) {
  const m = (method || "").toUpperCase();
  if (m === "GET") return "bg-blue-100 text-blue-700";
  if (m === "POST") return "bg-emerald-100 text-emerald-700";
  if (m === "PUT" || m === "PATCH") return "bg-amber-100 text-amber-700";
  if (m === "DELETE") return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
}

/**
 * Searchable endpoint picker. `endpoints` = flat catalog (FlatEndpoint[]);
 * `value` = current endpoint_id; `onChange(endpoint_id)`.
 */
export default function EndpointPicker({ endpoints, value, onChange, loading, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  const selected = useMemo(
    () => endpoints.find((e) => e.endpoint_id === value) || null,
    [endpoints, value]
  );

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return endpoints.slice(0, 80);
    return endpoints
      .filter((e) =>
        [e.endpoint_name, e.api_name, e.provider_name, e.http_method, e.path]
          .join(" ")
          .toLowerCase()
          .includes(t)
      )
      .slice(0, 80);
  }, [endpoints, q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white hover:border-gray-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
      >
        <span className="flex items-center gap-2 min-w-0">
          {selected ? (
            <>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold font-mono ${methodChip(selected.http_method)}`}>
                {selected.http_method}
              </span>
              <span className="truncate text-gray-800">{selected.endpoint_name}</span>
              <span className="text-gray-400 text-xs truncate">· {selected.provider_name}</span>
            </>
          ) : (
            <span className="text-gray-400">
              {loading ? "Loading endpoints…" : "Choose an endpoint…"}
            </span>
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search endpoints…"
              className="flex-1 text-sm focus:outline-none"
            />
          </div>
          <div className="overflow-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400">No endpoints found.</div>
            ) : (
              filtered.map((e) => (
                <button
                  key={e.endpoint_id}
                  type="button"
                  onClick={() => {
                    onChange(e.endpoint_id);
                    setOpen(false);
                    setQ("");
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold font-mono ${methodChip(e.http_method)}`}>
                    {e.http_method}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-gray-800 truncate">{e.endpoint_name}</span>
                    <span className="block text-xs text-gray-400 truncate">
                      {e.provider_name} · {e.api_name}
                    </span>
                  </span>
                  {e.endpoint_id === value && <Check className="w-4 h-4 text-blue-600" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
